import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from '../hooks/use-toast';
import { createQuoteRequest, createUnclaimedQuoteRequest } from '../services/api';

// Helper to check if phone is mobile
const isMobilePhone = (phone) => {
  if (!phone) return false;
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.startsWith('905') && cleaned.length === 12;
};

// Helper to format WhatsApp number
const formatWhatsAppNumber = (phone) => {
  if (!phone) return '';
  let cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('0')) cleaned = '90' + cleaned.substring(1);
  if (!cleaned.startsWith('90')) cleaned = '90' + cleaned;
  return cleaned;
};

const QuoteRequestDialog = ({ open, onOpenChange, venue }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    eventType: 'Söz',
    eventDate: '',
    guestCount: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.fullName || !formData.phone || !formData.email || !formData.eventDate || !formData.guestCount) {
      toast({
        title: 'Hata!',
        description: 'Lütfen tüm zorunlu alanları doldurun.',
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(true);

    try {
      // Check if venue is unclaimed with mobile phone (WhatsApp flow)
      const isUnclaimedWithMobile = venue.source === 'google' && !venue.isClaimed && isMobilePhone(venue.phone);
      
      // Check if venue is Pro/Premium plan with WhatsApp (WhatsApp flow)
      const isProPremiumWithWhatsApp = ['pro', 'premium'].includes(venue.currentPlan?.toLowerCase()) && 
                                       (venue.whatsappNumber || isMobilePhone(venue.phone));
      
      // Handle Pro/Premium plan with WhatsApp redirect
      if (isProPremiumWithWhatsApp) {
        const phoneNumber = venue.whatsappNumber || venue.phone;
        
        // Build WhatsApp message
        let message = `Merhaba,\n\ndavetevibulcom üzerinden ${venue.name} için teklif talebi oluşturdum.\n\n`;
        message += `Ad Soyad: ${formData.fullName}\n`;
        message += `Telefon: ${formData.phone}\n`;
        message += `E-posta: ${formData.email}\n`;
        message += `Etkinlik Tarihi: ${formData.eventDate}\n`;
        message += `Etkinlik Türü: ${formData.eventType}\n`;
        message += `Davetli Sayısı: ${formData.guestCount}\n`;
        
        if (formData.notes) {
          message += `\nNotlar: ${formData.notes}\n`;
        }
        
        message += `\nDetaylı bilgi ve teklif rica ederim.\n\nTeşekkürler.`;
        
        // Open WhatsApp
        const whatsappUrl = `https://wa.me/${formatWhatsAppNumber(phoneNumber)}?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
        
        toast({
          title: 'WhatsApp\'a Yönlendiriliyorsunuz!',
          description: 'Mekanla doğrudan iletişime geçmek için WhatsApp açılıyor...',
        });
        
        // Reset form and close
        setFormData({
          fullName: '',
          phone: '',
          email: '',
          eventType: 'Söz',
          eventDate: '',
          guestCount: '',
          notes: ''
        });
        onOpenChange(false);
        setSubmitting(false);
        return;
      }
      
      // Handle unclaimed venue with WhatsApp redirect
      if (isUnclaimedWithMobile) {
        const response = await createUnclaimedQuoteRequest({
          venueId: venue.id,
          fullName: formData.fullName,
          phone: formData.phone,
          eventType: formData.eventType,
          eventDate: formData.eventDate,
          guestCount: parseInt(formData.guestCount),
          notes: formData.notes || null
        });
        
        if (response.whatsappUrl) {
          window.open(response.whatsappUrl, '_blank');
          toast({
            title: 'WhatsApp\'a Yönlendiriliyorsunuz!',
            description: response.message || 'Mekanla doğrudan iletişime geçmek için WhatsApp açılıyor...',
          });
        }
        
        // Reset and close
        setFormData({
          fullName: '',
          phone: '',
          email: '',
          eventType: 'Söz',
          eventDate: '',
          guestCount: '',
          notes: ''
        });
        onOpenChange(false);
        setSubmitting(false);
        return;
      }

      // Regular flow - save to database
      const quoteData = {
        venueId: venue.id,
        fullName: formData.fullName,
        phone: formData.phone,
        email: formData.email,
        eventType: formData.eventType,
        eventDate: formData.eventDate || null,
        guestCount: formData.guestCount ? parseInt(formData.guestCount) : null,
        notes: formData.notes || null
      };

      await createQuoteRequest(quoteData);

      toast({
        title: 'Teklif Talebiniz Alındı!',
        description: 'Mekan sahibi en kısa sürede sizinle iletişime geçecektir.',
      });

      // Reset form and close
      setFormData({
        fullName: '',
        phone: '',
        email: '',
        eventType: 'Söz',
        eventDate: '',
        guestCount: '',
        notes: ''
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting quote request:', error);
      toast({
        title: 'Hata!',
        description: error.response?.data?.detail || 'Teklif talebi gönderilemedi. Lütfen tekrar deneyin.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Teklif Al - {venue?.name}</DialogTitle>
          <DialogDescription>
            Bilgilerinizi paylaşın, mekan sahibi en kısa sürede sizinle iletişime geçsin.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="fullName">Ad Soyad *</Label>
            <Input
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              placeholder="Ahmet Yılmaz"
              required
            />
          </div>

          <div>
            <Label htmlFor="phone">Telefon Numarası *</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="0555 123 45 67"
              required
            />
          </div>

          <div>
            <Label htmlFor="email">E-posta *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="ornek@email.com"
              required
            />
          </div>

          <div>
            <Label htmlFor="eventType">Organizasyon Türü *</Label>
            <Select
              value={formData.eventType}
              onValueChange={(value) => setFormData(prev => ({ ...prev, eventType: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seçiniz" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Nişan">Nişan</SelectItem>
                <SelectItem value="Söz">Söz</SelectItem>
                <SelectItem value="Kına Gecesi">Kına Gecesi</SelectItem>
                <SelectItem value="Doğum Günü">Doğum Günü</SelectItem>
                <SelectItem value="Bebek Mevlidi">Bebek Mevlidi</SelectItem>
                <SelectItem value="Bekarlığa Veda">Bekarlığa Veda</SelectItem>
                <SelectItem value="Kurumsal Etkinlik">Kurumsal Etkinlik</SelectItem>
                <SelectItem value="Diğer">Diğer</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="eventDate">Tahmini Etkinlik Tarihi</Label>
            <Input
              id="eventDate"
              name="eventDate"
              type="date"
              value={formData.eventDate}
              onChange={handleInputChange}
            />
          </div>

          <div>
            <Label htmlFor="guestCount">Tahmini Davetli Sayısı</Label>
            <Input
              id="guestCount"
              name="guestCount"
              type="number"
              value={formData.guestCount}
              onChange={handleInputChange}
              placeholder="150"
              min="0"
            />
          </div>

          <div>
            <Label htmlFor="notes">Notunuz / Merak Ettikleriniz</Label>
            <Textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Menü seçenekleri, kapasite, fiyat detayları veya merak ettiğiniz diğer konular hakkında sorularınızı buraya yazabilirsiniz..."
              rows={4}
            />
          </div>

          <div className="flex gap-3 justify-end pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              İptal
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-pink-600 hover:bg-rose-600"
            >
              {submitting ? 'Gönderiliyor...' : 'Teklif Al'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteRequestDialog;
